import {
    useCallback,
    useEffect,
    useEffectEvent,
    useMemo,
    useState
} from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useStableQuery } from '../../db/useStableQuery';
import { useForm, useWatch, type Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { v4 as uuid } from 'uuid';
import { useRxCollection, useLiveRxQuery } from 'rxdb/plugins/react';
import {
    findingPlanumInputSchema,
    isCircularForm,
    isStructureFinding,
    NO_ORIENTATION,
    PLANUM_PART_OPTIONS,
    ORIENTATION_OPTIONS,
    type FindingDocType,
    type FindingPlanumDocType,
    type FindingProfileDocType,
    type ParcelDocType
} from '@ast-data/shared';
import { useValidationMessage } from '../../i18n/validationMessages';
import { useSavedToast } from '../../components/toastContext';
import { useFamilyProjectIds } from '../projects/useFamilyProjectIds';
import { useCurrentPerson, usePersonUserId } from '../users/personUserId';
import { useSelfPrefill } from '../users/useSelfPrefill';
import { parcelPickerOptions } from './parcelPickerLabel';
import {
    isSelfReference,
    siblingStrataOptions,
    syncInverseStratigraphy,
    syncSymmetricRefs,
    isPlanumFieldRecommended,
    describedSlotIssue,
    siblingParts,
    renamedPartSiblings,
    MEASURE_PLANUM_FIELDS,
    HEIGHT_FIELDS,
    STRATIGRAPHY_FIELDS
} from './descriptionValidation';
import {
    asDescriptionBinding,
    DimensionsSection,
    IdentitySection,
    LocationSection,
    MediaSectionBox,
    SoilSection,
    StratigraphySection
} from './descriptionSections';
import { useFindingBasePath, useFindingEditPath } from './findingRefLabels';
import { useStrataRouteMap } from './strataRefRoutes';
import DescriptionPartBar, {
    type DescriptionPartEntry
} from './DescriptionPartBar';
import {
    toColorRows,
    cleanColors,
    colorsKey,
    type ColorRow
} from './colorRows';
import {
    toInclusionRows,
    cleanInclusions,
    inclusionsKey,
    type InclusionRow
} from './inclusionRows';
import {
    toStratumRows,
    cleanStrata,
    strataKey,
    type StratumRow
} from './stratumRows';
import {
    toCourseRows,
    cleanCourses,
    coursesKey,
    type CourseRow
} from './courseRows';
import { mergeSketchStrata, type SketchStrata } from './sketchStrata';
import { type FindingRefs, emptyFindingRefs } from './findingRefSets';
import FormRow from '../../components/FormRow';
import FormDialogAppBar from '../../components/FormDialogAppBar';
import RecordFormDialog from '../../components/RecordFormDialog';
import DescriptionReferencesSection from './DescriptionReferencesSection';
import {
    bindFormFields,
    labelIdsFrom,
    type FormFieldBinding,
    requiredInForm
} from '../../components/formFields';
import FormSection from '../../components/FormSection';
import { SectionNavMenuButton } from '../../components/formSectionNav';
import { type FormSection as FormSectionNavEntry } from '../../components/formSectionNavContext';
import { useAbility, scopedSubject } from '../auth/abilityContext';
import { useRecordTranslations } from '../translation/recordTranslations';
import { useUnsavedCloseGuard } from '../../components/useUnsavedCloseGuard';
import { useGuardedNavigate } from '../../components/useGuardedNavigate';
import { useFindingRefSupport } from './useFindingRefSupport';
import { useFindingSequence } from './useFindingSequence';
import {
    useForwardBackState,
    useDialogBackCrumb
} from '../../components/dialogBackTrail';
import { useFocusFieldOnOpen } from '../../components/cellFocus';

import { compareText } from '../../utils/compare';

// Tree-grid columns whose `field` differs from the Planum form input to focus.
const PLANUM_CELL_FOCUS_ALIASES: Record<string, string> = {
    date: 'recordedAt',
    parcelLabel: 'parcelId'
};

/**
 * Ordered sections for the in-form nav; must mirror the section boxes below.
 * The Media box always renders; the Verweise box only when editing (see
 * buildFormSections).
 */
const BASE_FORM_SECTIONS: FormSectionNavEntry[] = [
    { id: 'identity', titleId: 'planum.section.identity' },
    { id: 'location', titleId: 'planum.section.location' },
    { id: 'dimensions', titleId: 'planum.section.dimensions' },
    { id: 'soil', titleId: 'planum.section.soil' },
    { id: 'stratigraphy', titleId: 'planum.section.stratigraphy' },
    { id: 'media', titleId: 'media.title' }
];

function buildFormSections(
    isEdit: boolean,
    isStructure: boolean
): FormSectionNavEntry[] {
    // The Bodenmerkmale box is not rendered for a baulicher Bestand, and the
    // nav must not offer a jump to a section that is not there.
    const sections = isStructure
        ? BASE_FORM_SECTIONS.filter((section) => section.id !== 'soil')
        : BASE_FORM_SECTIONS;
    if (!isEdit) {
        return sections;
    }
    return [
        ...sections,
        { id: 'references', titleId: 'descriptionRefs.title' }
    ];
}

type FormValues = {
    findingId: string;
    parcelId: string;
    planumNumber: string;
    partLabel: string;
    trench: string;
    area: string;
    sondage: string;
    editor: string;
    length: string;
    width: string;
    diameter: string;
    depth: string;
    differenceToPreviousPlanum: string;
    heightTop: string;
    heightBottom: string;
    form: string;
    orientation: string;
    excavationMethod: string;
    soilType: string;
    consistency: string;
    boundaryClarity: string;
    boundaryBasis: string;
    contour: string;
    humusContent: string;
    overliesOrCuts: string;
    underliesOrIsCut: string;
    contemporaryWith: string;
    fills: string;
    filledBy: string;
    description: string;
    interpretation: string;
    recordedAt: string;
    phaseNumber: string;
};

function emptyValues(findingId: string): FormValues {
    return {
        findingId,
        parcelId: '',
        planumNumber: '',
        partLabel: '',
        trench: '',
        area: '',
        sondage: '',
        editor: '',
        length: '',
        width: '',
        diameter: '',
        depth: '',
        differenceToPreviousPlanum: '',
        heightTop: '',
        heightBottom: '',
        form: '',
        orientation: '',
        excavationMethod: '',
        soilType: '',
        consistency: '',
        boundaryClarity: '',
        boundaryBasis: '',
        contour: '',
        humusContent: '',
        overliesOrCuts: '',
        underliesOrIsCut: '',
        contemporaryWith: '',
        fills: '',
        filledBy: '',
        description: '',
        interpretation: '',
        recordedAt: new Date().toISOString(),
        phaseNumber: ''
    };
}

function toValues(planum: FindingPlanumDocType): FormValues {
    return {
        findingId: planum.findingId,
        parcelId: planum.parcelId,
        planumNumber: planum.planumNumber,
        partLabel: planum.partLabel,
        trench: planum.trench,
        area: planum.area,
        sondage: planum.sondage,
        editor: planum.editor,
        length: planum.length,
        width: planum.width,
        diameter: planum.diameter,
        depth: planum.depth,
        differenceToPreviousPlanum: planum.differenceToPreviousPlanum,
        heightTop: planum.heightTop,
        heightBottom: planum.heightBottom,
        form: planum.form,
        orientation: planum.orientation,
        excavationMethod: planum.excavationMethod,
        soilType: planum.soilType,
        consistency: planum.consistency,
        boundaryClarity: planum.boundaryClarity,
        boundaryBasis: planum.boundaryBasis,
        contour: planum.contour,
        humusContent: planum.humusContent,
        overliesOrCuts: planum.overliesOrCuts,
        underliesOrIsCut: planum.underliesOrIsCut,
        contemporaryWith: planum.contemporaryWith,
        fills: planum.fills,
        filledBy: planum.filledBy,
        description: planum.description,
        interpretation: planum.interpretation,
        recordedAt: planum.recordedAt,
        phaseNumber: planum.phaseNumber
    };
}

/**
 * A new Teil seeded off the one it was added from. What carries over belongs to
 * the PLANUM rather than to the piece of it — the Lage, the Form/Ausrichtung,
 * the Bodenmerkmale. What stays empty is what a Teil exists for: its own
 * measures, text, Schichten, stratigraphy, Aufnahmedatum and Verfasser, which
 * copied would look recorded without anyone having looked.
 */
function seededValues(
    source: FindingPlanumDocType,
    partLabel: string
): FormValues {
    return {
        ...emptyValues(source.findingId),
        parcelId: source.parcelId,
        planumNumber: source.planumNumber,
        partLabel,
        trench: source.trench,
        area: source.area,
        sondage: source.sondage,
        form: source.form,
        orientation: source.orientation,
        excavationMethod: source.excavationMethod,
        soilType: source.soilType,
        consistency: source.consistency,
        boundaryClarity: source.boundaryClarity,
        boundaryBasis: source.boundaryBasis,
        contour: source.contour,
        humusContent: source.humusContent,
        phaseNumber: source.phaseNumber
    };
}

function distinct(values: string[]): string[] {
    return [...new Set(values.filter(Boolean))].sort(
        (firstValue, secondValue) => compareText(firstValue, secondValue)
    );
}

interface FindingPlanumFormDialogProps {
    open: boolean;
    onClose: () => void;
    projectId: string;
    findingId: string;
    planum?: FindingPlanumDocType | null;
    /**
     * The Teil a new description was added from ("Teil hinzufügen" → `?seedFrom`).
     * Only read while creating; see {@link seededValues} for what it carries.
     */
    seedFrom?: FindingPlanumDocType | null;
    /** Grid column the user double-clicked, to focus the matching input on open. */
    focusField?: string;
}

/**
 * The Planum editor — a fullscreen dialog (better on mobile) whose sections are
 * Paper "boxes" like the Metadaten page, with a sticky Save/Cancel footer.
 * Mirrors FindingFormDialog; this form is long enough that the boxes carry most
 * of the benefit.
 */
/**
 * The person the record is written up by is required in the form — see
 * requiredInForm.
 */
const PLANUM_FORM_SCHEMA = requiredInForm(findingPlanumInputSchema, ['editor']);

export default function FindingPlanumFormDialog({
    open,
    onClose,
    projectId,
    findingId,
    planum,
    seedFrom,
    focusField
}: FindingPlanumFormDialogProps) {
    const intl = useIntl();
    const collection = useRxCollection<FindingPlanumDocType>('finding_planums');
    const validationMessage = useValidationMessage();
    const notifySaved = useSavedToast();
    const isEdit = !!planum;
    // A reader opens the record to look at it: the dialog shows it as a
    // sheet and has nothing to save (see RecordFormDialog).
    const readOnly = !useAbility().can(
        isEdit ? 'update' : 'create',
        scopedSubject('FindingPlanum', { projectId })
    );

    const ability = useAbility();
    const canEditMedia = ability.can(
        'update',
        scopedSubject('FindingPlanum', { projectId })
    );

    // Parcels + Befund numbers (for the picker + stratigraphy cross-refs) span
    // the whole Teilmaßnahme family, like the Befund form. Planum rows too:
    // the positional stratigraphy pickers list sibling descriptions on the same
    // Fläche/Planum, and a shared Befund's rows may sit in any family project.
    const familyIds = useFamilyProjectIds(projectId);
    const familyPlanumsQuery = useStableQuery<FindingPlanumDocType>({
        selector: { projectId: { $in: familyIds } }
    });
    const { results: familyPlanumsDocs } = useLiveRxQuery<FindingPlanumDocType>(
        {
            collection: 'finding_planums',
            query: familyPlanumsQuery
        }
    );
    const familyPlanums = useMemo(
        () =>
            familyPlanumsDocs.map(
                (doc) => doc.toMutableJSON() as FindingPlanumDocType
            ),
        [familyPlanumsDocs]
    );
    // Profil rows too: "Teil von / identisch mit" offers (and mirrors onto)
    // descriptions of both kinds across the family.
    const familyProfilesQuery = useStableQuery<FindingProfileDocType>({
        selector: { projectId: { $in: familyIds } }
    });
    const { results: familyProfilesDocs } =
        useLiveRxQuery<FindingProfileDocType>({
            collection: 'finding_profiles',
            query: familyProfilesQuery
        });
    const familyProfiles = useMemo(
        () =>
            familyProfilesDocs.map(
                (doc) => doc.toMutableJSON() as FindingProfileDocType
            ),
        [familyProfilesDocs]
    );
    const profileCollection =
        useRxCollection<FindingProfileDocType>('finding_profiles');
    // Backtrack trench/editor/area/sondage suggestions from THIS project's
    // rows only — spatial slots and crews differ between phases.
    const planums = useMemo(
        () => familyPlanums.filter((planum) => planum.projectId === projectId),
        [familyPlanums, projectId]
    );
    const profiles = useMemo(
        () =>
            familyProfiles.filter((profile) => profile.projectId === projectId),
        [familyProfiles, projectId]
    );
    const parcelsQuery = useStableQuery<ParcelDocType>({
        selector: { projectId: { $in: familyIds } }
    });
    const { results: parcelsDocs } = useLiveRxQuery<ParcelDocType>({
        collection: 'parcels',
        query: parcelsQuery
    });
    const parcels = useMemo(
        () => parcelsDocs.map((doc) => doc.toMutableJSON() as ParcelDocType),
        [parcelsDocs]
    );
    const findingsQuery = useStableQuery<FindingDocType>({
        selector: { projectId: { $in: familyIds } }
    });
    const { results: findingsDocs } = useLiveRxQuery<FindingDocType>({
        collection: 'findings',
        query: findingsQuery
    });
    const findings = useMemo(
        () => findingsDocs.map((doc) => doc.toMutableJSON() as FindingDocType),
        [findingsDocs]
    );

    const currentUserName = useCurrentPerson()?.name ?? '';
    // The links this record already carries, name by name: a name nobody
    // touched keeps its account even where the rule can no longer place it
    // (see usePersonUserId).
    const keptLinks = useMemo(
        () => (planum ? [{ name: planum.editor, id: planum.editorId }] : []),
        [planum]
    );
    const { resolve } = usePersonUserId(keptLinks);

    const suggestions = useMemo(
        () => ({
            trench: distinct([
                ...planums.map((planum) => planum.trench),
                ...profiles.map((profile) => profile.trench)
            ]),
            area: distinct([
                ...planums.map((planum) => planum.area),
                ...profiles.map((profile) => profile.area)
            ]),
            sondage: distinct([
                ...planums.map((planum) => planum.sondage),
                ...profiles.map((profile) => profile.sondage)
            ])
        }),
        [planums, profiles]
    );

    const ownFinding = useMemo(
        () => findings.find((finding) => finding.id === findingId) ?? null,
        [findings, findingId]
    );
    const ownFindingNumber = ownFinding?.findingNumber ?? '';
    // Whether this description belongs to a „baulicher Bestand" — the one thing
    // that changes what the form asks for. It is the BEFUND's type, set on the
    // Befund form and never here (see FINDING_TYPE_OPTIONS).
    const isStructure = isStructureFinding(ownFinding?.findingType);

    const befundEditPath = useFindingEditPath();

    const parcelOptions = useMemo(
        () => parcelPickerOptions(parcels),
        [parcels]
    );

    const {
        register,
        handleSubmit,
        reset,
        setError,
        setFocus,
        setValue,
        getValues,
        control,
        formState: { errors, isSubmitting, isDirty, dirtyFields }
    } = useForm<FormValues>({
        resolver: zodResolver(
            PLANUM_FORM_SCHEMA
        ) as unknown as Resolver<FormValues>,
        defaultValues: emptyValues(findingId)
    });

    // The Form governs two other fields, so it is watched: „kreisförmig" moves
    // the recommended-empty hint from Länge/Breite onto Durchmesser, and adds
    // „Keine" to what the Ausrichtung picker offers.
    const watchedForm = useWatch({ control, name: 'form' });
    const circular = isCircularForm(watchedForm);
    const orientationOptions = useMemo(
        () =>
            circular
                ? [NO_ORIENTATION, ...ORIENTATION_OPTIONS]
                : ORIENTATION_OPTIONS,
        [circular]
    );

    const fieldBinding: FormFieldBinding<FormValues> = {
        intl,
        readOnly,
        register,
        control,
        errors,
        validationMessage,
        labelId: labelIdsFrom('planum.field'),
        /**
         * The Abstich is in neither ExcaBook set — both describe what the sheet
         * expects, and the Abstich is ours alone. It is still a length in cm
         * and still worth a hint while empty, so it says so here.
         */
        recommended: (name) =>
            isPlanumFieldRecommended(name, watchedForm) ||
            name === 'differenceToPreviousPlanum',
        unitId: (name) => {
            if (HEIGHT_FIELDS.has(name)) {
                return 'common.metres';
            }
            if (
                MEASURE_PLANUM_FIELDS.has(name) ||
                name === 'differenceToPreviousPlanum'
            ) {
                return 'common.centimetres';
            }
            return undefined;
        }
    };
    const { text, suggest } = bindFormFields(fieldBinding);
    const sectionBinding = asDescriptionBinding(fieldBinding);

    // The Abstich names the Planum it was measured against.
    const differenceToPreviousField = (previousNumber: string) =>
        text('differenceToPreviousPlanum', {
            labelValues: { number: previousNumber }
        });

    // The three identity relations reference specific descriptions
    // (Planum/Profil), managed as ref arrays like the photo/OD Befund pickers.
    const linkedIdenticalRefs = useMemo<FindingRefs>(
        () => ({
            planumIds: planum?.identicalWithPlanumIds ?? [],
            profileIds: planum?.identicalWithProfileIds ?? [],
            findingIds: []
        }),
        [planum]
    );
    const [identicalRefs, setIdenticalRefs] =
        useState<FindingRefs>(emptyFindingRefs());
    const linkedPartOfRefs = useMemo<FindingRefs>(
        () => ({
            planumIds: planum?.partOfPlanumIds ?? [],
            profileIds: planum?.partOfProfileIds ?? [],
            findingIds: []
        }),
        [planum]
    );
    const [partOfRefs, setPartOfRefs] =
        useState<FindingRefs>(emptyFindingRefs());
    const linkedInProfileWithRefs = useMemo<FindingRefs>(
        () => ({
            planumIds: planum?.inProfileWithPlanumIds ?? [],
            profileIds: planum?.inProfileWithProfileIds ?? [],
            findingIds: []
        }),
        [planum]
    );
    const [inProfileWithRefs, setInProfileWithRefs] =
        useState<FindingRefs>(emptyFindingRefs());

    // The Farbe/Munsell pairs. Kept outside RHF, so they are folded into the
    // dirty check and the save by hand.
    const existingColors = useMemo<ColorRow[]>(
        () => toColorRows(planum?.colors ?? seedFrom?.colors),
        [planum, seedFrom]
    );
    const [colors, setColors] = useState<ColorRow[]>([]);

    // The Einschlüsse/Beimengungen — like the colours, each an inclusion word
    // plus an optional Anteil.
    const existingInclusions = useMemo<InclusionRow[]>(
        () => toInclusionRows(planum?.inclusions ?? seedFrom?.inclusions),
        [planum, seedFrom]
    );
    const [inclusions, setInclusions] = useState<InclusionRow[]>([]);

    // The Schichten and their own Länge/Breite. Local state like the two lists
    // above — and, unlike them, also written to from OUTSIDE the form: a Schicht
    // drawn on a sketch seeds its row here (see sketchStrata).
    const existingStrata = useMemo<StratumRow[]>(
        () => toStratumRows(planum?.strata),
        [planum]
    );
    const [strata, setStrata] = useState<StratumRow[]>([]);

    // The Lagen — what a baulicher Bestand is measured by instead of the flat
    // fields.
    const existingCourses = useMemo<CourseRow[]>(
        () => toCourseRows(planum?.courses),
        [planum]
    );
    const [courses, setCourses] = useState<CourseRow[]>([]);
    const stratumWord = intl.formatMessage({ id: 'sketch.stratum' });
    const seedStrata = useCallback(
        (sketches: SketchStrata[]) =>
            setStrata((rows) => mergeSketchStrata(rows, sketches, stratumWord)),
        // `setStrata` is listed although a setter is stable: the React Compiler
        // infers it as a dependency and refuses to keep a memo whose written
        // deps it cannot match (see react-hooks/preserve-manual-memoization).
        [stratumWord, setStrata]
    );

    // The positional relations (liegt über/schneidet, liegt unter/wird
    // geschnitten, zeitgleich mit) only make sense against descriptions on the
    // SAME Fläche and Planum — the options follow the form's current values.
    const [
        watchedArea,
        watchedPlanumNumber,
        watchedPartLabel,
        watchedFindingId
    ] = useWatch({
        control,
        name: ['area', 'planumNumber', 'partLabel', 'findingId']
    });

    // Every Planum description of the family, reduced to the slot it occupies
    // (Befund + Nummer + Teil) and carrying the route to its own editor. Both
    // the Teil bar and the slot rule read this; the family, not the project,
    // because a shared Befund's descriptions may sit in any of its projects.
    const findingBase = useFindingBasePath();
    const slotRows = useMemo(
        () =>
            familyPlanums.map((row) => ({
                id: row.id,
                findingId: row.findingId,
                number: row.planumNumber,
                partLabel: row.partLabel,
                to: `${findingBase(row.projectId, row.findingId)}/planums/${row.id}/edit`
            })),
        [familyPlanums, findingBase]
    );
    const partEntries = useMemo<DescriptionPartEntry[]>(
        () =>
            siblingParts(
                { findingId: watchedFindingId, number: watchedPlanumNumber },
                slotRows
            ),
        [slotRows, watchedFindingId, watchedPlanumNumber]
    );
    // The marks still FREE for this number: a Teil may exist only once (see
    // describedSlotIssue), so offering a taken one would only propose the error.
    // The open description's own mark stays, or editing it would look invalid.
    const freePartOptions = useMemo(
        () =>
            PLANUM_PART_OPTIONS.filter(
                (option) =>
                    !partEntries.some(
                        (entry) =>
                            entry.id !== (planum?.id ?? '') &&
                            entry.partLabel.trim().toLowerCase() ===
                                option.toLowerCase()
                    )
            ),
        [partEntries, planum]
    );
    // The family's Planum rows in the shape both the options and their routes
    // are read from: which slot each one occupies, and how to get to it.
    const strataSiblings = useMemo(
        () =>
            familyPlanums.map((planum) => ({
                id: planum.id,
                projectId: planum.projectId,
                findingId: planum.findingId,
                area: planum.area,
                number: planum.planumNumber
            })),
        [familyPlanums]
    );
    const positionalOptions = useMemo(
        () =>
            siblingStrataOptions({
                siblings: strataSiblings,
                findings,
                ownFindingId: findingId,
                area: watchedArea ?? '',
                number: watchedPlanumNumber ?? '',
                kind: 'Planum'
            }),
        [strataSiblings, findings, findingId, watchedArea, watchedPlanumNumber]
    );
    // Only the Befundnummer is stored, so a chip's link is resolved back to the
    // description it was picked from.
    const positionalRoutes = useStrataRouteMap({
        siblings: strataSiblings,
        findings,
        area: watchedArea ?? '',
        number: watchedPlanumNumber ?? '',
        kind: 'planum'
    });

    // The Planum with the next-lower number in the same Befund — what the
    // "Differenz zum vorherigen Planum" (Abstich in cm) refers to. Reacts to the
    // number as it is typed, so it appears from the 2nd Planum on.
    const previousPlanum = useMemo(() => {
        const current = (watchedPlanumNumber ?? '').trim();
        if (!current) {
            return null;
        }
        const below = familyPlanums.filter(
            (candidate) =>
                candidate.findingId === findingId &&
                candidate.id !== (planum?.id ?? '') &&
                candidate.planumNumber.trim() !== '' &&
                candidate.planumNumber
                    .trim()
                    .localeCompare(current, 'de', { numeric: true }) < 0
        );
        return (
            [...below].sort((firstPlanum, secondPlanum) =>
                secondPlanum.planumNumber
                    .trim()
                    .localeCompare(firstPlanum.planumNumber.trim(), 'de', {
                        numeric: true
                    })
            )[0] ?? null
        );
    }, [familyPlanums, findingId, planum, watchedPlanumNumber]);

    // The account names can arrive after the dialog opened, and the reset
    // below then had nothing to prefill with (see useSelfPrefill).
    useSelfPrefill(open && !planum, currentUserName, (self) => {
        if (getValues('editor').trim() === '') {
            setValue('editor', self);
        }
    });

    // An effect EVENT: every prefill source is read at its CURRENT value,
    // while the reset itself runs only when the dialog (re)opens for a
    // record — a background users-sync must not wipe what was typed.
    const resetForOpening = useEffectEvent(() => {
        const values = planum
            ? toValues(planum)
            : seedFrom
              ? // The Teil mark stays EMPTY and takes the focus (below): a
                /**
                 * proposed "A"/"1" is a guess at what the recorder called
                 * this piece in the field, and one they would have had to
                 * notice to correct. The picker still offers the convention.
                 */
                seededValues(seedFrom, '')
              : emptyValues(findingId);
        reset({
            ...values,
            /**
             * Default the Verfasser to whoever is recording — on a new
             * Planum, and on an older one that never got an editor. A name
             * already on the record is left alone.
             */
            editor: values.editor || currentUserName
        });
        setIdenticalRefs(linkedIdenticalRefs);
        setPartOfRefs(linkedPartOfRefs);
        setInProfileWithRefs(linkedInProfileWithRefs);
        setColors(existingColors);
        setInclusions(existingInclusions);
        setStrata(existingStrata);
        setCourses(existingCourses);
    });
    useEffect(() => {
        if (open) {
            // react-hook-form's `reset` publishes to its own subscribers, so
            // it cannot be moved into the render pass the way plain state can.
            // eslint-disable-next-line react-hooks/set-state-in-effect
            resetForOpening();
        }
    }, [open, planum, seedFrom, findingId]);

    /**
     * Picking „kreisförmig" fills the Ausrichtung in with „Keine" — a circle has
     * no long axis — and picking anything else takes that answer off again,
     * since no other Form may carry it.
     *
     * Only while the user has TOUCHED the Form field (`dirtyFields.form`), or
     * merely opening a round Planum would hand them a dialog that closes with an
     * "unsaved changes" prompt. And only over an EMPTY field or over the answer
     * it wrote itself, so a stated Ausrichtung is safe.
     */
    useEffect(() => {
        if (!dirtyFields.form) {
            return;
        }
        const orientation = (getValues('orientation') ?? '').trim();
        if (isCircularForm(watchedForm)) {
            if (orientation === '') {
                setValue('orientation', NO_ORIENTATION, { shouldDirty: true });
            }
        } else if (orientation === NO_ORIENTATION) {
            setValue('orientation', '', { shouldDirty: true });
        }
    }, [watchedForm, dirtyFields.form, getValues, setValue]);

    /**
     * Focus the input for the grid cell that was double-clicked to open this.
     * A seeded Teil opens with the cursor in the Teil field — it is the one
     * thing the seed cannot know and the only thing that tells this Planum apart
     * from the one it was seeded from.
     */
    useFocusFieldOnOpen(
        setFocus,
        open,
        focusField ?? (!planum && seedFrom ? 'partLabel' : undefined),
        PLANUM_CELL_FOCUS_ALIASES
    );

    // The three identity relations are local state, so fold them into the
    // dirty check — otherwise picking one and saving would look like a no-op.
    const identicalDirty = useMemo(() => {
        const key = (refs: FindingRefs) =>
            [...refs.planumIds].sort().join(',') +
            '|' +
            [...refs.profileIds].sort().join(',');
        return (
            key(identicalRefs) !== key(linkedIdenticalRefs) ||
            key(partOfRefs) !== key(linkedPartOfRefs) ||
            key(inProfileWithRefs) !== key(linkedInProfileWithRefs)
        );
    }, [
        identicalRefs,
        linkedIdenticalRefs,
        partOfRefs,
        linkedPartOfRefs,
        inProfileWithRefs,
        linkedInProfileWithRefs
    ]);

    const colorsDirty = useMemo(
        () =>
            colorsKey(cleanColors(colors)) !== colorsKey(planum?.colors ?? []),
        [colors, planum]
    );

    const inclusionsDirty = useMemo(
        () =>
            inclusionsKey(cleanInclusions(inclusions)) !==
            inclusionsKey(planum?.inclusions ?? []),
        [inclusions, planum]
    );

    // A sketch can add Schichten while the form is open, so this is what makes a
    // seeded row worth saving.
    const strataDirty = useMemo(
        () =>
            strataKey(cleanStrata(strata)) !== strataKey(planum?.strata ?? []),
        [strata, planum]
    );

    const coursesDirty = useMemo(
        () =>
            coursesKey(cleanCourses(courses)) !==
            coursesKey(planum?.courses ?? []),
        [courses, planum]
    );

    // Esc, browser back and the close/cancel buttons ask before dropping
    // unsaved edits (save / discard / keep editing).
    const translations = useRecordTranslations({
        ownerType: 'findingPlanum',
        ownerId: planum?.id,
        projectId,
        readOnly
    });
    const closeGuard = useUnsavedCloseGuard({
        open,
        isDirty:
            isDirty ||
            identicalDirty ||
            colorsDirty ||
            inclusionsDirty ||
            strataDirty ||
            coursesDirty ||
            translations.isDirty,
        onClose,
        onSave: () => handleSubmit(onSubmit)()
    });
    // Leaving for a referenced record honors unsaved edits (the close guard only
    // covers back/close/reload, not this in-app push). `forwardState` tags each
    // onward jump with this Planum so the target can offer a "back" here.

    // What this description is CALLED — "Planum 1", or "Planum 1, Teil A" once
    // it is one piece of a divided one. The number alone would name two Teile
    // alike, in the header and in the "zurück zu …" crumb that quotes it.
    const selfLabel = planum?.partLabel.trim()
        ? intl.formatMessage(
              { id: 'planum.labelPart' },
              {
                  number: planum.planumNumber || '?',
                  part: planum.partLabel.trim()
              }
          )
        : intl.formatMessage(
              { id: 'planum.label' },
              { number: planum?.planumNumber || '?' }
          );
    const guardedNav = useGuardedNavigate({
        isDirty:
            isDirty ||
            identicalDirty ||
            colorsDirty ||
            inclusionsDirty ||
            strataDirty ||
            coursesDirty ||
            translations.isDirty,
        submit: () => handleSubmit(onSubmit)(),
        close: () => closeGuard.close(),
        forwardState: useForwardBackState(selfLabel)
    });
    const backCrumb = useDialogBackCrumb(closeGuard.requestBack);
    // Step through the Befunde with their Plana and Profile in between —
    // through the nav guard, like any other link out of an open form (see
    // useFindingSequence).
    const siblings = useFindingSequence({
        projectId,
        currentId: planum?.id ?? '',
        requestNavigate: guardedNav.requestNavigate
    });

    // The Befunde the running description names, marked and linked — the same
    // editor the Tagebuch's prose fields carry. Defined AFTER `guardedNav`: a
    // reference is a link out of an open editor, so it has to ask before
    // leaving, exactly as the Verweise chips do.
    const findingRefs = useFindingRefSupport(
        projectId,
        guardedNav.requestNavigate
    );

    async function onSubmit(data: FormValues) {
        if (readOnly) {
            return;
        }
        // The slot this description claims within its Befund: Planum 2 is
        // described once, or in Teile that are each described once — never both
        // (see describedSlotIssue). Checked across the family, because a shared
        // Befund's rows may sit in any of its projects.
        const planumNumber = data.planumNumber.trim();
        const partLabel = data.partLabel.trim();
        const slotIssue = describedSlotIssue(
            {
                id: planum?.id ?? '',
                findingId: data.findingId,
                number: planumNumber,
                partLabel
            },
            slotRows
        );
        if (slotIssue) {
            setError(
                slotIssue.target === 'part' ? 'partLabel' : 'planumNumber',
                { type: 'validate', message: slotIssue.code },
                { shouldFocus: true }
            );
            return;
        }
        // The stratigraphy fields cross-reference OTHER Befunde — this Planum's
        // own Befund may not appear among them.
        const selfReferencing = [...STRATIGRAPHY_FIELDS].filter((name) =>
            isSelfReference(
                String(data[name as keyof FormValues] ?? ''),
                ownFindingNumber
            )
        );
        if (selfReferencing.length > 0) {
            selfReferencing.forEach((name, index) => {
                setError(
                    name as keyof FormValues,
                    { type: 'validate', message: 'selfReference' },
                    { shouldFocus: index === 0 }
                );
            });
            return;
        }

        const now = new Date().toISOString();
        // A stable own id (an insert's uuid up front) so the identity sync can
        // mirror this description's ref onto the ones it points at.
        const ownId = isEdit ? planum!.id : uuid();
        const content = {
            ...data,
            planumNumber,
            partLabel,
            colors: cleanColors(colors),
            inclusions: cleanInclusions(inclusions),
            strata: cleanStrata(strata),
            courses: cleanCourses(courses),
            editorId: resolve(data.editor),
            identicalWithPlanumIds: identicalRefs.planumIds,
            identicalWithProfileIds: identicalRefs.profileIds,
            partOfPlanumIds: partOfRefs.planumIds,
            partOfProfileIds: partOfRefs.profileIds,
            inProfileWithPlanumIds: inProfileWithRefs.planumIds,
            inProfileWithProfileIds: inProfileWithRefs.profileIds
        };
        // The sketch's north tool patches the north fields on its own. The zod
        // resolver injects defaults for them, so a Save drops them rather than
        // clobbering the stored arrow.
        const northless = content as Record<string, unknown>;
        delete northless.northArrow;
        delete northless.northAngle;
        if (isEdit && collection) {
            const doc = await collection.findOne(ownId).exec();
            if (doc) {
                await doc.incrementalPatch({ ...content, updatedAt: now });
            }
        } else if (collection) {
            await collection.insert({
                id: ownId,
                projectId,
                ...content,
                northArrow: false,
                northAngle: 0,
                northX: 88,
                northY: 12,
                createdBy: '',
                updatedBy: '',
                updatedAt: now,
                createdAt: now
            });
        }
        await translations.commit(ownId);
        // A Planum recorded in Teile carries ONE number: correcting it here
        // corrects it on the other Teile too, or the description falls apart
        // into pieces of two different numbers (see renamedPartSiblings).
        if (collection && isEdit) {
            for (const siblingId of renamedPartSiblings({
                self: {
                    id: ownId,
                    findingId: data.findingId,
                    number: planumNumber,
                    partLabel
                },
                previous: {
                    findingId: planum!.findingId,
                    number: planum!.planumNumber
                },
                siblings: slotRows
            })) {
                const sibling = await collection.findOne(siblingId).exec();
                if (sibling) {
                    await sibling.incrementalPatch({
                        planumNumber: planumNumber,
                        updatedAt: now
                    });
                }
            }
        }
        // Mirror the positional relations onto the referenced Befunde ("A über
        // B" writes "B unter A" on B's description of the same Fläche/Planum).
        if (collection) {
            await syncInverseStratigraphy({
                siblings: familyPlanums.map((planum) => ({
                    id: planum.id,
                    findingId: planum.findingId,
                    area: planum.area,
                    slotNumber: planum.planumNumber,
                    overliesOrCuts: planum.overliesOrCuts,
                    underliesOrIsCut: planum.underliesOrIsCut,
                    contemporaryWith: planum.contemporaryWith,
                    fills: planum.fills,
                    filledBy: planum.filledBy
                })),
                findings,
                ownFindingId: data.findingId,
                before: {
                    area: planum?.area ?? '',
                    slotNumber: planum?.planumNumber ?? '',
                    overliesOrCuts: planum?.overliesOrCuts ?? '',
                    underliesOrIsCut: planum?.underliesOrIsCut ?? '',
                    contemporaryWith: planum?.contemporaryWith ?? '',
                    fills: planum?.fills ?? '',
                    filledBy: planum?.filledBy ?? ''
                },
                after: {
                    area: data.area,
                    slotNumber: data.planumNumber,
                    overliesOrCuts: data.overliesOrCuts,
                    underliesOrIsCut: data.underliesOrIsCut,
                    contemporaryWith: data.contemporaryWith,
                    fills: data.fills,
                    filledBy: data.filledBy
                },
                applyPatch: async (id, patch) => {
                    const sibling = await collection.findOne(id).exec();
                    if (sibling) {
                        await sibling.incrementalPatch({
                            ...patch,
                            updatedAt: now
                        });
                    }
                }
            });
            // Both symmetric relations close over the GROUP they name: four
            // descriptions listed as identical are identical with each other,
            // so all four end up carrying the other three.
            for (const relation of [
                'identicalWith',
                'inProfileWith'
            ] as const) {
                await syncSymmetricRefs({
                    relation,
                    rows: [
                        ...familyPlanums.map((planum) => ({
                            id: planum.id,
                            kind: 'planum' as const,
                            identicalWithPlanumIds:
                                planum.identicalWithPlanumIds,
                            identicalWithProfileIds:
                                planum.identicalWithProfileIds,
                            inProfileWithPlanumIds:
                                planum.inProfileWithPlanumIds,
                            inProfileWithProfileIds:
                                planum.inProfileWithProfileIds
                        })),
                        ...familyProfiles.map((profile) => ({
                            id: profile.id,
                            kind: 'profile' as const,
                            identicalWithPlanumIds:
                                profile.identicalWithPlanumIds,
                            identicalWithProfileIds:
                                profile.identicalWithProfileIds,
                            inProfileWithPlanumIds:
                                profile.inProfileWithPlanumIds,
                            inProfileWithProfileIds:
                                profile.inProfileWithProfileIds
                        }))
                    ],
                    own: { id: ownId, kind: 'planum' },
                    before:
                        relation === 'identicalWith'
                            ? {
                                  planumIds:
                                      planum?.identicalWithPlanumIds ?? [],
                                  profileIds:
                                      planum?.identicalWithProfileIds ?? []
                              }
                            : {
                                  planumIds:
                                      planum?.inProfileWithPlanumIds ?? [],
                                  profileIds:
                                      planum?.inProfileWithProfileIds ?? []
                              },
                    after:
                        relation === 'identicalWith'
                            ? {
                                  planumIds: identicalRefs.planumIds,
                                  profileIds: identicalRefs.profileIds
                              }
                            : {
                                  planumIds: inProfileWithRefs.planumIds,
                                  profileIds: inProfileWithRefs.profileIds
                              },
                    applyPatch: async (kind, id, patch) => {
                        const target =
                            kind === 'planum' ? collection : profileCollection;
                        const sibling = await target?.findOne(id).exec();
                        if (sibling) {
                            await sibling.incrementalPatch({
                                ...patch,
                                updatedAt: now
                            });
                        }
                    }
                });
            }
        }
        notifySaved();
        // Go to a "save and go" reference target if one is pending, else close
        // (guard-aware: a routed dialog steps over the guard's history sentinel).
        guardedNav.finishSubmit();
    }

    return (
        <RecordFormDialog
            open={open}
            sections={buildFormSections(isEdit, isStructure)}
            onSubmit={handleSubmit(onSubmit)}
            closeGuard={closeGuard}
            navGuard={guardedNav}
            isSubmitting={isSubmitting}
            readOnly={readOnly}
            translations={translations}
            appBar={
                <FormDialogAppBar
                    projectId={projectId}
                    onClose={closeGuard.requestClose}
                    startSlot={<SectionNavMenuButton />}
                    backCrumb={backCrumb}
                    findingCrumb={
                        findingId
                            ? {
                                  number: ownFindingNumber,
                                  onOpen: () =>
                                      guardedNav.requestNavigate(
                                          befundEditPath(projectId, findingId)
                                      ),
                                  // Also a real link, so the Befund can be
                                  // opened in a new tab from here.
                                  to: befundEditPath(projectId, findingId)
                              }
                            : undefined
                    }
                    siblings={siblings}
                    title={
                        isEdit && planum?.planumNumber ? (
                            selfLabel
                        ) : (
                            <FormattedMessage
                                id={
                                    isEdit
                                        ? 'planum.form.editTitle'
                                        : 'planum.form.newTitle'
                                }
                            />
                        )
                    }
                />
            }
        >
            <IdentitySection
                binding={sectionBinding}
                titlePrefix="planum.section"
                numberField={text('planumNumber')}
                partField={suggest('partLabel', freePartOptions)}
                partBar={
                    <DescriptionPartBar
                        parts={partEntries}
                        currentId={planum?.id ?? null}
                        currentLabel={watchedPartLabel}
                        onOpen={guardedNav.requestNavigate}
                        onAdd={
                            planum && canEditMedia
                                ? () =>
                                      // Saves this Teil first — the sibling is
                                      // seeded from the STORED record, so an
                                      // unsaved mark would seed a clash.
                                      void guardedNav.saveAndNavigate(
                                          `${findingBase(
                                              planum.projectId,
                                              planum.findingId
                                          )}/planums/new?seedFrom=${planum.id}`
                                      )
                                : undefined
                        }
                    />
                }
                findingRefs={findingRefs}
                findingType={ownFinding?.findingType}
            />
            <LocationSection
                binding={sectionBinding}
                titlePrefix="planum.section"
                parcelOptions={parcelOptions}
                suggestions={suggestions}
            />
            <DimensionsSection
                binding={sectionBinding}
                titlePrefix="planum.section"
                labelPrefix="planum.field"
                measures={
                    <>
                        <FormRow>
                            {text('length')}
                            {text('width')}
                        </FormRow>
                        <FormRow>
                            {text('diameter')}
                            {text('depth')}
                        </FormRow>
                        {/* From the 2nd Planum on: the vertical
                                        drop in cm to the previous (next-lower)
                                        Planum. */}
                        {previousPlanum && (
                            <FormRow>
                                {differenceToPreviousField(
                                    previousPlanum.planumNumber
                                )}
                            </FormRow>
                        )}
                    </>
                }
                orientationOptions={orientationOptions}
                strata={strata}
                onStrataChange={setStrata}
                strataLength
                strataDepth={false}
                courses={isStructure ? courses : undefined}
                onCoursesChange={isStructure ? setCourses : undefined}
            />
            {/* A baulicher Bestand has no Bodenmerkmale — a wall is not a soil
                matrix. Anything recorded before stays in the document; it is
                only never asked for again. */}
            {!isStructure && (
                <SoilSection
                    binding={sectionBinding}
                    titlePrefix="planum.section"
                    colors={colors}
                    onColorsChange={setColors}
                    inclusions={inclusions}
                    onInclusionsChange={setInclusions}
                />
            )}
            <StratigraphySection
                binding={sectionBinding}
                titlePrefix="planum.section"
                positionalOptions={positionalOptions}
                positionalRoutes={positionalRoutes}
                projectId={projectId}
                findingId={findingId}
                inProfileWith={inProfileWithRefs}
                onInProfileWithChange={setInProfileWithRefs}
                identical={identicalRefs}
                onIdenticalChange={setIdenticalRefs}
                partOf={partOfRefs}
                onPartOfChange={setPartOfRefs}
                onOpenRef={guardedNav.requestNavigate}
                showFilling={isStructure}
            />
            <MediaSectionBox
                projectId={projectId}
                ownerType="findingPlanum"
                ownerId={isEdit && planum ? planum.id : null}
                canEdit={canEditMedia}
                location={{
                    findingNumber: ownFindingNumber,
                    area: watchedArea,
                    planumNumber: watchedPlanumNumber
                }}
                // A baulicher Bestand is measured in Lagen, so the Schichten tool
                // would seed a list it does not have.
                strataTool={!isStructure}
                onSketchStrata={isStructure ? undefined : seedStrata}
                onNavigate={guardedNav.requestNavigate}
            />

            {isEdit && planum && (
                <FormSection anchorId="references">
                    <DescriptionReferencesSection
                        descriptionId={planum.id}
                        findingId={planum.findingId}
                        findingNumber={ownFindingNumber}
                        kind="planum"
                        projectId={projectId}
                        onNavigate={guardedNav.requestNavigate}
                        canEdit={canEditMedia}
                    />
                </FormSection>
            )}
        </RecordFormDialog>
    );
}
